import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentTenant,
  listTenants,
  me,
  setCurrentTenant,
  type AdminMe,
} from "../lib/api";

/**
 * Sélecteur de tenant courant pour le back-office multi-tenant.
 *
 * - SUPER_ADMIN voit tous les musées (+ une option "Tous les musées" = vue globale).
 * - Les autres rôles voient seulement les musées auxquels ils sont rattachés
 *   et doivent toujours en choisir un.
 *
 * Le slug courant est stocké en localStorage et attaché en header `X-Tenant-Slug`
 * par `lib/api.ts`. Toute mutation du tenant invalide les queries pour rafraîchir
 * les listes (POIs, expos, médias, etc.).
 */
export function TenantSwitcher() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const meQuery = useQuery<AdminMe>({ queryKey: ["admin", "me"], queryFn: me });
  const tenantsQuery = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: listTenants,
    // Tous les rôles connectés peuvent voir au moins leurs propres tenants
    // (le back filtre déjà par membership).
    enabled: meQuery.isSuccess,
  });

  const current = getCurrentTenant();
  const isSuperAdmin = meQuery.data?.role === "SUPER_ADMIN";

  // Liste des tenants visibles pour le user courant
  const visibleTenants =
    tenantsQuery.data?.filter((t) =>
      isSuperAdmin ? true : meQuery.data?.tenantSlugs.includes(t.slug),
    ) ?? [];

  const currentTenant = visibleTenants.find((t) => t.slug === current) ?? null;

  // Ferme le menu au clic en dehors
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const select = (slug: string | null) => {
    setCurrentTenant(slug);
    setOpen(false);
    // Invalide tout le scope admin → relit avec le bon header X-Tenant-Slug.
    void qc.invalidateQueries({ queryKey: ["admin"] });
  };

  // Label affiché dans le bouton
  let buttonLabel: string;
  let buttonHint: string;
  if (currentTenant) {
    buttonLabel = currentTenant.name;
    buttonHint = currentTenant.shortCode;
  } else if (isSuperAdmin) {
    buttonLabel = "Tous les clients";
    buttonHint = "Vue globale";
  } else {
    buttonLabel = "Sélectionner un client…";
    buttonHint = "—";
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border border-[color:var(--color-alto-border)] bg-white hover:bg-[color:var(--color-alto-bg)] text-left transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-alto-muted)]">
            Client
          </p>
          <p className="text-sm font-medium truncate">{buttonLabel}</p>
        </div>
        <span className="text-xs font-mono text-[color:var(--color-alto-muted)] shrink-0">
          {buttonHint}
        </span>
        <svg
          aria-hidden
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={"shrink-0 transition-transform " + (open ? "rotate-180" : "")}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute top-full mt-1 left-0 right-0 z-20 max-h-72 overflow-y-auto bg-white border border-[color:var(--color-alto-border)] rounded-md shadow-lg py-1"
        >
          {isSuperAdmin && (
            <li>
              <button
                type="button"
                onClick={() => select(null)}
                className={
                  "w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--color-alto-bg)] " +
                  (current === null ? "bg-[color:var(--color-alto-bg)] font-medium" : "")
                }
              >
                <span className="block">Tous les clients</span>
                <span className="block text-xs text-[color:var(--color-alto-muted)]">
                  Vue globale (SUPER_ADMIN)
                </span>
              </button>
            </li>
          )}
          {visibleTenants.length === 0 && !isSuperAdmin && (
            <li className="px-3 py-2 text-sm text-[color:var(--color-alto-muted)]">
              Aucun client rattaché à votre compte.
            </li>
          )}
          {visibleTenants.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => select(t.slug)}
                className={
                  "w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--color-alto-bg)] " +
                  (current === t.slug ? "bg-[color:var(--color-alto-bg)] font-medium" : "")
                }
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate">{t.name}</span>
                  <span className="text-xs font-mono text-[color:var(--color-alto-muted)] shrink-0">
                    {t.shortCode}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
